import React, { useState, useEffect, useRef } from "react";
import Layout from "../../components/Layout";
import { useParams, useNavigate, useBeforeUnload } from "react-router-dom";
import axios from "axios";
import PrintPreviewModal from "../../components/PrintPreviewModal";
import {
  faTimes,
  faFileLines,
  faPrint,
  faCircleCheck,
  faCirclePlus,
  faCircleInfo,
  faBars,
  faCircleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ConfirmModal from "../../components/ConfirmModal";
import SuccessModal from "../../components/SuccessModal";
import ErrorModal from "../../components/ErrorModal";
import { debounce } from "lodash";
import { useCallback } from "react";
import "./Sheet.css";

type User = {
  id: number;
  last_name: string;
  first_name: string;
  last_name_en: string;
  first_name_en: string;
  name_ini: string;
  name: string;
  name_en: string;
  nationality: string;
  postal_code: string;
  gender: string;
  age: number;
  birth_date: string;
  current_address: string;
  qualifications?: {
    id: string;
    name: string;
    pivot: {
      qualification_id: number;
      qualification_get_date: string;
    };
  }[];
  sheets: {
    updated_at: string;
  };
  isSelected: boolean;
  isTarget: boolean;
};

type Qualification = {
  id: string | "";
  qualification_id: string | null;
  name: string | null;
  get_date: string | null;
  isDisplay: boolean;
  rowKey?: string; // UI用の一意キー（API送信時は除外）
};

type Specialty = {
  sheet_id: number;
  specialty: string;
};

type TechFolio = {
  id: string | null;
  specialty: string;
  tech_experiences?: {
    id: string | null;
    skill_type: number;
    sequence_num: number;
    industry: string;
    system_name: string;
    system_detail: string;
    assigned_task: string;
    work_contents?: {
      id: number;
      name: string;
      rowKey?: string; // UI用の一意キー（API送信時は除外）
    }[];
    position_id: number;
    position?: {
      id: string | null;
      name: string;
    };
    team: string;
    member_count: string;
    env1: string;
    env2: string;
    env3: string;
    env4: string;
    env5: string;
    work_start_period: string;
    work_end_period: string;
    period_year: number | null;
    period_month: number | null;
    client: string;
    end_user: string;
    remarks: string;
  }[];
};

type WorkContent = {
  id: number;
  experience_id: number;
  work_content_id: number;
};

type TechExperience = {
  id: number;
  skill_type: number;
  sequence_num: number;
  industry: string;
  system_name: string;
  system_detail: string;
  assigned_task: string;
  work_contents: WorkContent[];
  position_id: number;
  team: string;
  member_count: string;
  env1?: string;
  env2?: string;
  env3?: string;
  env4?: string;
  env5?: string;
  start_date: Date | null;
  end_date: Date | null;
  period_year: number | null;
  period_month: number | null;
  client: string;
  end_user: string;
  remarks: string;
};

// 技術経験シート中の作業内容と役割の選択肢の型定義
type SelectOption = {
  id: number;
  name: string;
};

const MAX_LENGTH = {
  input: {
    default: 50,
    postal: 7, // 郵便番号：ハイフンを除く7桁
  },
  textarea: {
    default: 255,
    specialty: 365, // 得意分野入力欄：全角60文字×最大6行+改行5つ
  },
};

const MAX_NEWLINES = {
  specialty: 6,
};

const MySheet: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [qualificationOptions, setQualificationOptions] = useState<
    SelectOption[]
  >([]);
  const [qualifications, setQualifications] = useState<Qualification[]>([]);
  const [techFolio, setTechFolio] = useState<TechFolio | null>(null);
  const [workContentOptions, setWorkContentOptions] = useState<SelectOption[]>(
    [],
  );
  const [positionOptions, setPositionOptions] = useState<SelectOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const { userId } = useParams();
  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);
  const [isErrorModalOpen, setErrorModalOpen] = useState(false);
  const [modalText, setModalText] = useState(""); // 確認モーダル用
  const [confirmAction, setConfirmAction] = useState<() => void>(() => {});
  const [isDirty, setIsDirty] = useState(false);
  const navigate = useNavigate();

  // 技術経験テーブルのヘッダーがsticky状態かどうか
  const [isHeaderStuck, setIsHeaderStuck] = useState(false);
  const techExpSentinelRef = useRef<HTMLDivElement>(null);

  // 技術経験テーブルのヘッダーがsticky状態になったかを検知
  useEffect(() => {
    const sentinel = techExpSentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // sentinelが見えなくなったらヘッダーがsticky状態
        setIsHeaderStuck(!entry.isIntersecting);
      },
      { threshold: 0 },
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [isLoading]);

  // ページ全体を監視
  useEffect(() => {
    // 入力内容の変更時
    const handleChange = (e: Event) => {
      const target = e.target as HTMLElement;
      const tag = target.tagName;
      // 特定の入力系要素は対象
      if (["INPUT", "TEXTAREA", "SELECT"].includes(tag)) {
        setIsDirty((prev) => (prev ? prev : true));
      }
    };

    // ボタンのクリック時
    const handleClick = (e: Event) => {
      const target = e.target as HTMLElement;
      // 特定クラス（ignore-dirtyとmodal）のボタンは対象外
      if (target.closest(".ignore-dirty")) return;
      if (target.closest(".modal")) return;

      // 上記以外のボタンクリックはすべて対象
      if (target.closest("button")) {
        setIsDirty((prev) => (prev ? prev : true));
      }
    };

    document.addEventListener("input", handleChange);
    document.addEventListener("change", handleChange);
    document.addEventListener("click", handleClick);

    return () => {
      document.removeEventListener("input", handleChange);
      document.removeEventListener("change", handleChange);
      document.removeEventListener("click", handleClick);
    };
  }, []);

  // ブラウザを閉じる / タブを閉じる / ページをリロードするときに警告を出す
  useBeforeUnload((event: BeforeUnloadEvent) => {
    if (isDirty) {
      event.preventDefault();
      event.returnValue = "";
    }
  });

  // アプリ内で他ページに遷移するときに警告を出す
  const handleNavigate = (path: string) => {
    if (
      isDirty &&
      !window.confirm("行った変更が保存されない可能性があります。")
    )
      return;
    navigate(path);
  };

  const getLabelsBySkillType = (skill_type: number): string[] => {
    switch (skill_type) {
      case 1:
        return ["ServerOS", "MW/ツール", "開発言語", "DB", "開発環境"];
      case 2:
        return ["基盤環境", "クラウド", "OS基盤", "MW構成", "運用監視"];
      case 3:
        return ["対象製品/サービス", "管理方法", "管理対象"];
      case 4:
        return ["ServerOS", "MW/ツール", "開発言語", "PJ管理", "開発環境"];
      default:
        return ["環境1", "環境2", "環境3", "環境4", "環境5"];
    }
  };

  // 初回データ取得メソッド
  const fetchSheetData = async () => {
    try {
      const response = await axios.get<{
        user: User;
        qualifications: Qualification[];
        techFolio: TechFolio;
        specialty: Specialty;
        techExperiences: TechExperience[];
        qualificationOptions: SelectOption[];
        workContentOptions: SelectOption[];
        positionOptions: SelectOption[];
      }>(
        `${process.env.REACT_APP_BACKEND_URL}/api/tf/my-sheet/data/get?userId=${userId}`,
        {
          withCredentials: true,
        },
      );

      const {
        user,
        techFolio,
        qualificationOptions,
        workContentOptions,
        positionOptions,
      } = response.data;

      // ユーザープロフィールセット
      setUser(user);

      // マスタリストをセット
      setQualificationOptions(qualificationOptions);
      setWorkContentOptions(workContentOptions);
      setPositionOptions(positionOptions);

      // ユーザーの資格情報を分離してセット（資格情報がある場合のみ）
      const formattedQualifications =
        user.qualifications?.map((q) => ({
          id: q.id,
          qualification_id: q.pivot.qualification_id.toString(),
          name: q.name,
          get_date: q.pivot.qualification_get_date,
          isDisplay: true,
          rowKey: crypto.randomUUID(), // UI用の一意キー
        })) || [];
      setQualifications(formattedQualifications);

      // 技術経験セット（nullを空文字に正規化、work_contentsにrowKeyを付与）
      const normalizedTechFolio: TechFolio = {
        ...techFolio,
        specialty: techFolio.specialty ?? "",
        tech_experiences: techFolio.tech_experiences?.map((exp) => ({
          ...exp,
          industry: exp.industry ?? "",
          system_name: exp.system_name ?? "",
          system_detail: exp.system_detail ?? "",
          assigned_task: exp.assigned_task ?? "",
          team: exp.team ?? "",
          member_count: exp.member_count ?? "",
          env1: exp.env1 ?? "",
          env2: exp.env2 ?? "",
          env3: exp.env3 ?? "",
          env4: exp.env4 ?? "",
          env5: exp.env5 ?? "",
          work_start_period: exp.work_start_period ?? "",
          work_end_period: exp.work_end_period ?? "",
          client: exp.client ?? "",
          end_user: exp.end_user ?? "",
          remarks: exp.remarks ?? "",
          work_contents: exp.work_contents?.map((wc) => ({
            ...wc,
            rowKey: crypto.randomUUID(), // UI用の一意キー
          })),
        })),
      };
      setTechFolio(normalizedTechFolio);
    } catch (error) {
      console.error("データの取得に失敗しました:", error);
      setErrorModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSheetData();
  }, []);

  // ユーザ情報変更（住所のみ変更可能）
  const handleChangeProfile = (field: keyof User, value: string) => {
    setUser((prevUser) => {
      if (!prevUser) return null;
      return { ...prevUser, [field]: value };
    });
  };

  // 資格情報変更
  const handleChangeQualification = (
    idx: number,
    field: "qualification_id" | "get_date",
    value: string,
  ) => {
    // 該当フィールドのエラーをクリア
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[`qualifications.${idx}.${field}`];
      return newErrors;
    });

    const updatedQualifications = qualifications.map((qualification, index) => {
      if (index === idx) {
        // qualification_idを変更する場合
        if (field === "qualification_id") {
          const selectedOption = qualificationOptions.find(
            (option) => String(option.id) === value,
          );
          return {
            ...qualification,
            qualification_id: value,
            name: selectedOption?.name || "", // 選択された資格名を更新
          };
        }

        // get_dateを変更する場合
        if (field === "get_date") {
          return {
            ...qualification,
            get_date: `${value}-01`, // 月に対して1日を追加
          };
        }
      }
      return qualification;
    });

    setQualifications(updatedQualifications);
  };

  // 資格追加
  const handleAddQualification = () => {
    // 資格関連のエラーをクリア（インデックスがずれるため）
    setErrors((prev) => {
      const newErrors = { ...prev };
      Object.keys(newErrors).forEach((key) => {
        if (key.startsWith("qualifications.")) {
          delete newErrors[key];
        }
      });
      return newErrors;
    });

    setQualifications([
      ...qualifications,
      {
        id: "",
        qualification_id: null,
        name: null,
        get_date: null,
        isDisplay: true,
        rowKey: crypto.randomUUID(), // UI用の一意キー
      },
    ]);
  };

  // 資格削除（rowKeyで特定して削除）
  const handleDeleteQualification = (rowKey: string) => {
    // 資格関連のエラーをクリア（インデックスがずれるため）
    setErrors((prev) => {
      const newErrors = { ...prev };
      Object.keys(newErrors).forEach((key) => {
        if (key.startsWith("qualifications.")) {
          delete newErrors[key];
        }
      });
      return newErrors;
    });

    setQualifications((prev) => prev.filter((q) => q.rowKey !== rowKey));
  };

  // 得意分野変更
  const handleChangeSpecialty = (value: string) => {
    setTechFolio((prevTechFolio) => {
      if (!prevTechFolio) return null;
      const normalizedValue = value.replace(/\r\n|\r/g, "\n");
      const lines = normalizedValue.split("\n");
      const prevLines = prevTechFolio.specialty
        ? prevTechFolio.specialty.split("\n")
        : [];

      // ペーストで一括入力された場合、最大改行数までに制限して反映
      if (lines.length - prevLines.length > 1) {
        const limitedValue = lines.slice(0, MAX_NEWLINES.specialty).join("\n");
        return { ...prevTechFolio, specialty: limitedValue };
      }

      // 入力で最大改行数を超えた場合は、改行を無視して値を更新しない
      if (lines.length > MAX_NEWLINES.specialty) {
        return prevTechFolio;
      }

      return { ...prevTechFolio, specialty: normalizedValue };
    });
  };

  // 技術経験行追加
  const handleAddTechExperience = (skill_type: number) => {
    setTechFolio((prevTechFolio) => {
      if (!prevTechFolio || !prevTechFolio.tech_experiences) {
        throw new Error("TechFolio or tech_experiences is undefined.");
      }
      const newSequenceNum =
        Math.max(
          ...prevTechFolio.tech_experiences.map((tech) => tech.sequence_num),
          0,
        ) + 1;
      const newTechExperience = {
        id: null,
        skill_type: skill_type,
        sequence_num: newSequenceNum,
        industry: "",
        system_name: "",
        system_detail: "",
        assigned_task: "",
        work_contents: [
          {
            id: 0,
            name: "",
          },
        ],
        position_id: 1,
        position: {
          id: "1",
          name: "PM",
        },
        team: "PJTチーム",
        member_count: "",
        env1: "",
        env2: "",
        env3: "",
        env4: "",
        env5: "",
        start_date: null,
        end_date: null,
        period_year: null,
        period_month: null,
        client: "",
        end_user: "",
        remarks: "",
        work_start_period: "",
        work_end_period: "",
      };

      return {
        ...prevTechFolio,
        tech_experiences: [
          ...prevTechFolio.tech_experiences,
          newTechExperience,
        ],
      };
    });
  };

  // 技術経験削除
  const handleDeleteTechExperience = (seqNum: number) => {
    setTechFolio((prevTechFolio) => {
      if (!prevTechFolio || !prevTechFolio.tech_experiences)
        return prevTechFolio;
      // 削除対象を除外
      const filteredTechExperiences = prevTechFolio.tech_experiences.filter(
        (tech) => tech.sequence_num !== seqNum,
      );
      // 削除された sequence_num より大きいものの sequence_num を -1
      const updatedTechExperiences = filteredTechExperiences.map((tech) =>
        tech.sequence_num > seqNum
          ? { ...tech, sequence_num: tech.sequence_num - 1 }
          : tech,
      );
      // 状態を更新
      return { ...prevTechFolio, tech_experiences: updatedTechExperiences };
    });
  };

  // 技術経験変更
  const handleChangeTechExperience = (
    seqNum: number,
    field: string,
    value: any,
  ) => {
    setTechFolio((prevTechFolio) => {
      if (!prevTechFolio || !prevTechFolio.tech_experiences)
        return prevTechFolio;

      // `tech_experiences` 配列を新しい値で更新
      const updatedTechExperiences = prevTechFolio.tech_experiences.map(
        (tech) =>
          tech.sequence_num === seqNum
            ? { ...tech, [field]: value } // 対象オブジェクトのみ更新
            : tech, // 他のオブジェクトはそのまま
      );

      // 更新した配列を返す
      return { ...prevTechFolio, tech_experiences: updatedTechExperiences };
    });
  };

  // ポジション変更
  const handleChangePosition = (
    seqNum: number,
    field: string,
    value: any,
    label: string,
  ) => {
    setTechFolio((prevTechFolio) => {
      if (!prevTechFolio || !prevTechFolio.tech_experiences)
        return prevTechFolio;

      // `tech_experiences` 配列を新しい値で更新
      const updatedTechExperiences = prevTechFolio.tech_experiences.map(
        (tech) =>
          tech.sequence_num === seqNum
            ? {
                ...tech,
                [field]: {
                  ...tech.position,
                  id: value.toString(),
                  name: label,
                },
              } // 対象オブジェクトのみ更新
            : tech, // 他のオブジェクトはそのまま
      );

      // 更新した配列を返す
      return { ...prevTechFolio, tech_experiences: updatedTechExperiences };
    });
  };

  // 作業内容追加
  const handleAddWorkContent = (seqNum: number) => {
    setTechFolio((prevTechFolio) => {
      if (!prevTechFolio || !prevTechFolio.tech_experiences) {
        throw new Error("TechFolio or tech_experiences is undefined.");
      }

      // 新しい作業内容を作成（rowKeyを付与）
      const newWorkContent = {
        id: 0,
        name: "",
        rowKey: crypto.randomUUID(), // UI用の一意キー
      };

      // `tech_experiences` を更新
      const updatedTechExperiences = prevTechFolio.tech_experiences.map(
        (tech) => {
          if (tech.sequence_num !== seqNum) {
            return tech; // 対象外の技術経験はそのまま
          }

          // 新しい作業内容を追加
          const updatedWorkContents = [
            ...(tech.work_contents || []),
            newWorkContent,
          ];

          return { ...tech, work_contents: updatedWorkContents };
        },
      );

      return { ...prevTechFolio, tech_experiences: updatedTechExperiences };
    });
  };

  // 作業内容行削除（rowKeyで特定して削除）
  const handleDeleteWorkContent = (seqNum: number, rowKey: string) => {
    setTechFolio((prevTechFolio) => {
      if (!prevTechFolio || !prevTechFolio.tech_experiences) {
        throw new Error("TechFolio or tech_experiences is undefined.");
      }
      // `tech_experiences` を更新
      const updatedTechExperiences = prevTechFolio.tech_experiences.map(
        (tech) => {
          if (tech.sequence_num !== seqNum) {
            return tech; // 対象外の技術経験はそのまま
          }
          // `work_contents` を更新 (指定されたrowKeyを削除)
          const updatedWorkContents =
            tech.work_contents?.filter((wc) => wc.rowKey !== rowKey) ?? [];
          return { ...tech, work_contents: updatedWorkContents };
        },
      );
      return { ...prevTechFolio, tech_experiences: updatedTechExperiences };
    });
  };

  // 作業内容変更
  const handleChangeWorkContent = (
    seqNum: number,
    wcIdx: number,
    value: number,
    label: string,
  ) => {
    setTechFolio((prevTechFolio) => {
      if (!prevTechFolio || !prevTechFolio.tech_experiences) {
        throw new Error("TechFolio or tech_experiences is undefined.");
      }
      // 指定された sequence_num の tech_experience を更新
      const updatedTechExperiences = prevTechFolio.tech_experiences.map(
        (tech) => {
          if (tech.sequence_num !== seqNum) {
            return tech; // 対象外の技術経験はそのまま
          }
          // work_contents を更新
          const updatedWorkContents =
            tech.work_contents?.map((workContent, idx) => {
              if (idx !== wcIdx) {
                return workContent; // 対象外の作業内容はそのまま
              }
              // 作業内容を更新
              return { ...workContent, id: value, name: label };
            }) ?? [];
          return { ...tech, work_contents: updatedWorkContents };
        },
      );
      return { ...prevTechFolio, tech_experiences: updatedTechExperiences };
    });
  };

  // ドラッグの開始
  const handleDragStart = (
    e: React.DragEvent<HTMLTableCellElement>,
    seqNum: number,
  ) => {
    const rowElement = e.currentTarget.parentElement as HTMLTableRowElement;

    // 行全体のドラッグイメージを作成
    const dragImage = rowElement.cloneNode(true) as HTMLTableRowElement;
    dragImage.style.backgroundColor = "#f0f0f0"; // 背景色を設定
    dragImage.style.width = `${rowElement.offsetWidth}px`; // 元の行幅を維持
    dragImage.style.position = "absolute"; // 必須: ドラッグイメージは絶対位置
    dragImage.style.top = "-9999px"; // ドラッグ時に見えない場所に配置

    document.body.appendChild(dragImage); // ドラッグイメージを DOM に追加

    // ドラッグイメージを設定
    e.dataTransfer.setDragImage(dragImage, 0, 0);

    // データをセット
    e.dataTransfer.setData("text/plain", seqNum.toString());

    // ドラッグ終了時にクリーンアップ
    const handleDragEnd = (event: Event) => {
      if (event.currentTarget) {
        const target = event.currentTarget as HTMLElement;
        target.removeEventListener("dragend", handleDragEnd);
      }
      document.body.removeChild(dragImage);
    };

    if (e.currentTarget) {
      e.currentTarget.addEventListener("dragend", handleDragEnd);
    }
  };

  // ドラッグ中の要素の上に来た際の処理
  const handleDragOver = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault(); // ドロップを許可
  };

  // ドロップ後の処理
  const handleDrop = (
    e: React.DragEvent<HTMLElement>,
    targetSeqNum: number,
  ) => {
    e.preventDefault();
    const draggedSeqNum = parseInt(e.dataTransfer.getData("text/plain"));

    setTechFolio((prevTechFolio) => {
      if (!prevTechFolio || !prevTechFolio.tech_experiences)
        return prevTechFolio;

      // `tech_experiences`を並び替え
      const updatedTechExperiences = prevTechFolio.tech_experiences.map(
        (tech) => {
          if (tech.sequence_num === draggedSeqNum) {
            return { ...tech, sequence_num: targetSeqNum };
          }
          if (tech.sequence_num === targetSeqNum) {
            return { ...tech, sequence_num: draggedSeqNum };
          }
          return tech;
        },
      );

      // `sequence_num`でソート
      updatedTechExperiences.sort((a, b) => a.sequence_num - b.sequence_num);

      return { ...prevTechFolio, tech_experiences: updatedTechExperiences };
    });
  };

  // テキストエリアの高さを自動調整
  const handleTextareaAutoResize = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const textarea = e.target;
    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  // テキストエリアの初期高さを自動調整（マウント時）
  const initTextareaAutoResize = (textarea: HTMLTextAreaElement | null) => {
    if (textarea && textarea.value) {
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  // 記入例別タブ表示
  const showExample = () => {
    window.open("/tf/sheet-example", "_blank");
  };

  // // 1つ前の履歴別タブ表示
  // const showHistory = () => {
  //   console.log("1つ前の履歴別タブ表示を別タブ表示");
  // };

  // 保存
  const handleSave = () => {
    setModalText("変更内容を保存します。");
    setConfirmAction(() => async () => {
      try {
        setIsSaving(true);
        // API送信前にrowKeyを除外
        const qualificationsForApi = qualifications.map(
          ({ rowKey, ...rest }) => rest,
        );
        const techFolioForApi = techFolio
          ? {
              ...techFolio,
              tech_experiences: techFolio.tech_experiences?.map((exp) => ({
                ...exp,
                work_contents: exp.work_contents?.map(
                  ({ rowKey, ...rest }) => rest,
                ),
              })),
            }
          : null;
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/tf/save-data`,
          {
            user,
            qualifications: qualificationsForApi,
            tech_folio: techFolioForApi,
          },
          { withCredentials: true },
        );
        await fetchSheetData();
        setSuccessModalOpen(true);
        setIsDirty(false); // 未保存フラグをリセット
      } catch (error: any) {
        console.error("保存エラー:", error);
        if (error.response?.status === 422) {
          setErrors(error.response.data.errors);
          setErrorMessage(
            "入力内容に誤りがあります。各項目を確認してください。",
          );
          setErrorModalOpen(true);
        } else {
          setErrorModalOpen(true);
        }
      } finally {
        setIsSaving(false);
      }
    });
    setConfirmModalOpen(true);
  };

  // 印刷オプション入力モーダルを開く
  const openPrevModal = () => {
    setModalOpen(true);
  };

  // work_end_periodが未来の日付かどうか
  const isPlaned = (idx: number) => {
    // 年月形式の文字列を "YYYY-MM-01" の形式に変換
    const current = new Date();
    const end = new Date(
      techFolio?.tech_experiences?.[idx - 1].work_end_period + "-01",
    );

    return end > current;
  };

  // 開始日と終了日から期間を計算
  const calculatePeriod = (idx: number) => {
    // 年月形式の文字列を "YYYY-MM-01" の形式に変換
    const start = new Date(
      techFolio?.tech_experiences?.[idx - 1].work_start_period + "-01",
    );
    const end = new Date(
      techFolio?.tech_experiences?.[idx - 1].work_end_period + "-30",
    );

    // 差をミリ秒で計算し、月数に変換
    const diffTime: number = end.getTime() - start.getTime(); // 明示的に number 型として計算
    const diffMonths = diffTime / (1000 * 60 * 60 * 24 * 30); // 月単位の差

    // 月数から年と月を計算
    const years = Math.floor(diffMonths / 12);
    const months = Math.round(diffMonths % 12);

    return `${years}年 ${months}ヶ月`;
  };

  // 開始日と終了日から期間を計算
  const calculateBirthDate = (date: string) => {
    return new Date(date).toLocaleDateString("ja-JP", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // ユーザー情報更新
  const handleInputChange = (field: keyof User, value: any) => {
    setUser((prevUser) => {
      return prevUser ? { ...prevUser, [field]: value } : null;
    });
  };

  // 郵便番号から住所検索
  const fetchAddressByPostalCode = useCallback(
    debounce(async (zip: string) => {
      try {
        const response = await axios.get(
          `https://zipcloud.ibsnet.co.jp/api/search?zipcode=${zip}`,
        );

        const result = response.data as {
          results:
            | { address1: string; address2: string; address3: string }[]
            | null;
          message: string;
        };

        if (result.results && result.results.length > 0) {
          const data = result.results[0];
          handleInputChange(
            "current_address",
            `${data.address1}${data.address2}${data.address3}`,
          );
        } else {
          setErrorMessage("該当する住所が見つかりませんでした。");
          setErrorModalOpen(true);
        }
      } catch (error) {
        console.error("住所取得エラー:", error);
        setErrorMessage("住所の取得に失敗しました。");
        setErrorModalOpen(true);
      }
    }, 300), // 300msのデバウンス
    [],
  );

  // 郵便番号検索のためMyPage.tsxより追加
  const [errorMessage, setErrorMessage] = useState<string>(
    "入力内容に誤りがあるか、予期せぬエラーが発生しました。",
  );
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  return (
    <Layout>
      <div className="p-10 bg-white rounded-3xl">
        <div className="flex mb-4">
          <div className="title-text font-black">スキルシート</div>
        </div>
        {isLoading || isSaving ? (
          <div>
            <div className="flex mb-6 space-x-20">
              {/* プロフィールテーブルスケルトン */}
              <div className="w-2/5">
                <div className="flex mb-4">
                  <div className="rounded-full mt-1.5 border-slate-100 mr-3 w-5 h-5 title-circle"></div>
                  <div className="text-lg text-slate-500 font-black">
                    プロフィール
                  </div>
                </div>
                <div className="w-11/12 border-2 border-slate-300 rounded-2xl overflow-hidden">
                  <table className="table-auto text-slate-600 w-full border-collapse">
                    <tbody>
                      <tr>
                        <th className="w-32 bg-slate-100 px-4 py-2 text-right border-r-2">
                          氏名
                        </th>
                        <td className="bg-slate-50">
                          <div className="w-[95%] mx-auto h-5 bg-slate-200 rounded w-24 animate-pulse"></div>
                        </td>
                      </tr>
                      <tr>
                        <th className="bg-eaf0f6 px-4 py-2 text-right border-r-2">
                          ローマ字
                        </th>
                        <td className="bg-slate-50">
                          <div className="w-[95%] mx-auto h-5 bg-slate-200 rounded w-24 animate-pulse"></div>
                        </td>
                      </tr>
                      <tr>
                        <th className="bg-slate-100 px-4 py-2 text-right border-r-2">
                          国籍
                        </th>
                        <td className="bg-slate-50">
                          <div className="w-[95%] mx-auto h-5 bg-slate-200 rounded w-24 animate-pulse"></div>
                        </td>
                      </tr>
                      <tr>
                        <th className="bg-eaf0f6 px-4 py-2 text-right border-r-2">
                          性別
                        </th>
                        <td className="bg-slate-50">
                          <div className="w-[95%] mx-auto h-5 bg-slate-200 rounded w-24 animate-pulse"></div>
                        </td>
                      </tr>
                      {/* <tr>
                        <th className="bg-slate-100 px-4 py-2 text-right border-r-2">
                          年齢
                        </th>
                        <td className="bg-slate-50">
                          <div className="w-[95%] mx-auto h-5 bg-slate-200 rounded w-24 animate-pulse"></div>
                        </td>
                      </tr> */}
                      <tr>
                        <th className="bg-slate-100 px-4 py-2 text-right border-r-2">
                          生年月日
                        </th>
                        <td className="bg-slate-50">
                          <div className="w-[95%] mx-auto h-5 bg-slate-200 rounded w-24 animate-pulse"></div>
                        </td>
                      </tr>
                      <tr>
                        <th className="bg-eaf0f6 px-4 py-2 text-right border-r-2">
                          現住所
                          <br />
                          <span className="font-normal">(市区まで記入)</span>
                        </th>
                        <td className="bg-slate-50">
                          <div className="w-[95%] mx-auto h-10 bg-slate-200 rounded w-24 animate-pulse"></div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              {/* 保有資格テーブルスケルトン */}
              <div className="w-3/5">
                <div className="flex mb-4">
                  <div className="rounded-full mt-1.5 border-slate-100 mr-3 w-5 h-5 title-circle"></div>
                  <div className="text-lg text-slate-500 font-black">
                    保有資格
                  </div>
                </div>
                <div className="w-11/12 border-2 border-slate-300 rounded-2xl overflow-hidden">
                  <table className="table-auto text-slate-600 w-full border-collapse">
                    <tbody>
                      {[...Array(3)].map((_, idx) => (
                        <tr key={idx} className="bg-slate-50">
                          <td colSpan={4} className="h-16">
                            <div className="w-[95%] mx-auto h-10 bg-slate-200 rounded w-24 animate-pulse"></div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* ボタンスケルトン */}
                <div className="w-28 h-10 mt-2 bg-slate-200 rounded-3xl animate-pulse"></div>
              </div>
            </div>
            {/* 得意分野テーブルスケルトン */}
            <div className="mb-7">
              <div className="flex mb-4">
                <div className="rounded-full mt-1.5 border-slate-100 mr-3 w-5 h-5 title-circle"></div>
                <div className="text-lg text-slate-500 font-black">
                  得意分野
                </div>
              </div>
              <div className="h-[11.5em] w-[800px] bg-slate-50 resize-none border-2 border-slate-300 rounded-2xl animate-pulse">
                <div className="w-[95%] mt-4 mx-auto h-24 bg-slate-200 rounded w-24 animate-pulse"></div>
              </div>
            </div>
            {/* 技術経験テーブルスケルトン */}
            <div className="mb-6">
              <div className="flex mb-4">
                <div className="rounded-full mt-1.5 border-slate-100 mr-3 w-5 h-5 title-circle"></div>
                <div className="text-lg text-slate-500 font-black">
                  経験プロジェクト
                </div>
              </div>
              <div className="w-full border-2 border-slate-300 rounded-2xl overflow-hidden">
                <table className="table-auto text-slate-600 w-full border-collapse">
                  <tbody>
                    {[...Array(4)].map((_, idx) => (
                      <tr key={idx} className="bg-slate-50">
                        <td colSpan={8} className="h-60">
                          <div className="w-[98%] mx-auto h-48 bg-slate-200 rounded w-24 animate-pulse"></div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex mb-6 space-x-20">
              {/* プロフィールテーブル */}
              <div className="min-w-[20rem]">
                <div className="flex mb-4">
                  <div className="rounded-full mt-1.5 border-slate-100 mr-3 w-5 h-5 title-circle"></div>
                  <div className="text-lg text-slate-500 font-black">
                    プロフィール
                  </div>
                </div>
                <div className="border-2 border-slate-300 rounded-2xl inline-block overflow-x-auto">
                  <table className="table-auto w-auto text-slate-600 border-collapse">
                    <tbody>
                      <tr>
                        <th className="w-32 bg-slate-100 px-4 py-2 text-right border-r-2">
                          氏名
                        </th>
                        <td className="bg-white  px-4 py-2">
                          {user ? `${user.name}` : "データがありません"}
                        </td>
                      </tr>
                      <tr>
                        <th className="bg-eaf0f6 px-4 py-2 text-right border-r-2">
                          ローマ字
                        </th>
                        <td className="bg-slate-50  px-4 py-2">
                          {user ? user.name_en : "データなし"}
                        </td>
                      </tr>
                      <tr>
                        <th className="bg-slate-100 px-4 py-2 text-right border-r-2">
                          国籍
                        </th>
                        <td className="bg-white  px-4 py-2">
                          {user ? user.nationality : "データなし"}
                        </td>
                      </tr>
                      <tr>
                        <th className="bg-eaf0f6 px-4 py-2 text-right border-r-2">
                          性別
                        </th>
                        <td className="bg-slate-50  px-4 py-2">
                          {user ? user.gender : "データなし"}
                        </td>
                      </tr>
                      {/* <tr>
                        <th className="bg-slate-100 px-4 py-2 text-right border-r-2">
                          年齢
                        </th>
                        <td className="bg-white  px-4 py-2">
                          {user ? user.age : "データなし"}
                        </td>
                      </tr> */}
                      <tr>
                        <th className="bg-slate-100 px-4 py-2 text-right border-r-2">
                          生年月日
                        </th>
                        <td className="bg-white  px-4 py-2">
                          {user
                            ? calculateBirthDate(user.birth_date)
                            : "データなし"}
                        </td>
                      </tr>
                      <tr>
                        <th className="w-40 bg-eaf0f6 px-4 py-2 text-right border-r-2">
                          郵便番号
                          <br />
                          <span className="font-normal text-xs">
                            (ハイフンなし7桁)
                          </span>
                        </th>
                        <td className="bg-slate-50 px-4 py-2">
                          <div className="flex">
                            <input
                              type="text"
                              inputMode="numeric"
                              maxLength={MAX_LENGTH.input.postal}
                              value={user?.postal_code || ""}
                              placeholder="例: 1000001"
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                              onChange={(e) => {
                                const zip = e.target.value.replace(
                                  /[^\d]/g,
                                  "",
                                );
                                handleInputChange("postal_code", zip);

                                if (zip.length === 7) {
                                  fetchAddressByPostalCode(zip);
                                }
                              }}
                            />
                          </div>
                          {errors["user.postal_code"] && (
                            <p className="text-red-500 text-sm mt-1">
                              <FontAwesomeIcon
                                icon={faCircleExclamation}
                                className="mr-1"
                              />
                              {errors["user.postal_code"][0]}
                            </p>
                          )}
                        </td>
                      </tr>

                      <tr>
                        <th className="w-40 bg-slate-100 px-4 py-2 text-right border-r-2">
                          現住所
                        </th>
                        <td className="bg-white px-4 py-2">
                          <div className="flex">
                            <input
                              type="text"
                              value={user?.current_address || ""}
                              readOnly
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed"
                            />
                          </div>
                          {errors["user.current_address"] && (
                            <p className="text-red-500 text-sm mt-1">
                              <FontAwesomeIcon
                                icon={faCircleExclamation}
                                className="mr-1"
                              />
                              {errors["user.current_address"][0]}
                            </p>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              {/* 保有資格テーブル */}
              <div className="min-w-[40rem]">
                <div className="flex mb-4">
                  <div className="rounded-full mt-1.5 border-slate-100 mr-3 w-5 h-5 title-circle"></div>
                  <div className="text-lg text-slate-500 font-black">
                    保有資格
                  </div>
                </div>
                <div className="border-2 border-slate-300 rounded-2xl inline-block overflow-x-auto">
                  <table className="table-auto w-auto text-slate-600 border-collapse">
                    <thead className="border-b-2">
                      <tr>
                        <th className="bg-slate-100 px-4 py-2 border-r-2">
                          項番
                        </th>
                        <th className="bg-slate-100 px-4 py-2 border-r-2">
                          資格名称
                        </th>
                        <th className="bg-slate-100 px-4 py-2 border-r-2">
                          取得年月
                        </th>
                        <th className="bg-slate-100 px-4 py-2">削除</th>
                      </tr>
                    </thead>
                    <tbody>
                      {qualifications.length === 0 ? (
                        <tr>
                          <td
                            colSpan={4}
                            className="px-4 py-4 text-center text-slate-400 text-sm bg-white"
                          >
                            保有資格が登録されていません。
                          </td>
                        </tr>
                      ) : (
                        qualifications.map((qualification, idx) => {
                          // 既に選択されている資格IDの配列を作成
                          const selectedIds = qualifications
                            .filter((q, index) => index !== idx) // 現在の項目以外を対象
                            .map((q) => q.qualification_id);

                          // フィルタリングされた選択肢
                          const filteredOptions = qualificationOptions.filter(
                            (option) =>
                              !selectedIds.includes(String(option.id)),
                          );

                          return (
                            <tr key={qualification.rowKey}>
                              <td
                                className={`${
                                  idx % 2 === 0 ? "bg-slate-100" : "bg-eaf0f6"
                                } px-4 py-2 text-center border-r-2`}
                              >
                                {idx + 1}
                              </td>
                              <td
                                className={`${
                                  idx % 2 === 0 ? "bg-white" : "bg-slate-50"
                                } px-4 py-2 border-r-2`}
                              >
                                <select
                                  value={qualification.qualification_id || ""}
                                  onChange={(e) =>
                                    handleChangeQualification(
                                      idx,
                                      "qualification_id",
                                      e.target.value,
                                    )
                                  }
                                  className="border-2 border-slate-300 px-2 py-1 rounded focus:outline-none focus:ring focus:border-blue-300"
                                >
                                  <option value="" disabled>
                                    資格を選択
                                  </option>
                                  {filteredOptions.map((option) => (
                                    <option key={option.id} value={option.id}>
                                      {option.name}
                                    </option>
                                  ))}
                                </select>
                                {errors[
                                  `qualifications.${idx}.qualification_id`
                                ] && (
                                  <p className="text-red-500 text-sm mt-1">
                                    {
                                      errors[
                                        `qualifications.${idx}.qualification_id`
                                      ][0]
                                    }
                                  </p>
                                )}
                              </td>
                              <td
                                className={`${
                                  idx % 2 === 0 ? "bg-white" : "bg-slate-50"
                                } px-4 py-2 border-r-2`}
                              >
                                <input
                                  type="month"
                                  value={
                                    qualification.get_date
                                      ? qualification.get_date.slice(0, 7)
                                      : ""
                                  }
                                  onChange={(e) =>
                                    handleChangeQualification(
                                      idx,
                                      "get_date",
                                      e.target.value,
                                    )
                                  }
                                  className="border border-gray-300 rounded p-2"
                                />
                                {errors[`qualifications.${idx}.get_date`] && (
                                  <p className="text-red-500 text-sm mt-1">
                                    {
                                      errors[
                                        `qualifications.${idx}.get_date`
                                      ][0]
                                    }
                                  </p>
                                )}
                              </td>
                              <td
                                className={`${
                                  idx % 2 === 0 ? "bg-white" : "bg-slate-50"
                                } px-4 py-2`}
                              >
                                <div className="flex items-center justify-center h-full">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleDeleteQualification(
                                        qualification.rowKey!,
                                      )
                                    }
                                    className="flex items-center justify-center w-6 h-6 bg-slate-600 text-white rounded-full hover:bg-slate-300"
                                  >
                                    <FontAwesomeIcon icon={faTimes} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="w-11/12 flex py-2">
                  <button
                    onClick={handleAddQualification}
                    className="bg-gray-700 text-white font-bold py-2 px-4 mr-20 rounded-3xl hover:bg-opacity-50"
                  >
                    <FontAwesomeIcon icon={faCirclePlus} className="mr-2" />
                    資格追加
                  </button>
                  <div className="flex items-center">
                    <FontAwesomeIcon
                      icon={faCircleInfo}
                      className="text-slate-400 mt-1"
                      style={{ fontSize: "1.25rem" }} // 高さ調整
                    />
                    <div className="flex flex-col ml-2 space-y-0.5">
                      <span className="text-slate-400 text-xs">
                        有効期限が失効した資格も削除せずに記載してください
                      </span>
                      <span className="text-slate-400 text-xs">
                        資格が選択肢にない場合は管理者に連絡してください
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* 得意分野 */}
            <div className="mb-6">
              <div className="flex mb-4">
                <div className="rounded-full mt-1.5 border-slate-100 mr-3 w-5 h-5 title-circle"></div>
                <div className="text-lg text-slate-500 font-black">
                  得意分野
                </div>
              </div>
              <textarea
                onChange={(e) => handleChangeSpecialty(e.target.value)}
                className="h-[11.5em] w-[800px] p-3 resize-none border-2 border-slate-300 rounded-2xl"
                value={techFolio?.specialty ?? ""}
                maxLength={MAX_LENGTH.textarea.specialty}
              />
            </div>
            {/* 技術経験テーブル */}
            <div className="mb-6">
              <div className="flex mb-4 items-center justify-between">
                <div className="flex">
                  <div className="rounded-full mt-1.5 border-slate-100 mr-3 w-5 h-5 title-circle"></div>
                  <div className="text-lg text-slate-500 font-black">
                    経験プロジェクト
                  </div>
                </div>
              </div>
              {/* ヘッダーのsticky状態を検知するためのsentinel要素 */}
              <div ref={techExpSentinelRef} className="h-0" />
              <div className="tech-exp-wrap w-full border-2 border-slate-300 rounded-2xl bg-white">
                <div className="overflow-visible">
                  <table
                    className={`tech-exp-table table-auto text-slate-600 w-full border-collapse ${
                      isHeaderStuck ? "tech-exp-table--stuck" : ""
                    }`}
                  >
                    <thead className="border-b-2">
                      <tr>
                        <th className="bg-slate-200 px-2 py-2 border-r-2 w-16">
                          項番
                        </th>
                        <th className="bg-slate-200 px-2 py-2 border-r-2 min-w-60">
                          担当作業・作成プログラム等
                        </th>
                        <th className="bg-slate-200 px-2 py-2 border-r-2 w-56">
                          作業内容
                        </th>
                        <th className="bg-slate-200 px-2 py-2 border-r-2 w-44">
                          参加形態
                        </th>
                        <th className="bg-slate-200 px-2 py-2 border-r-2 w-60">
                          環境
                        </th>
                        <th className="bg-slate-200 px-2 py-2 border-r-2 w-36">
                          作業期間
                        </th>
                        <th className="bg-slate-200 px-2 py-2 border-r-2 min-w-40">
                          補足事項
                          <br />
                          （印刷対象外）
                        </th>
                        <th className="bg-slate-200 px-2 py-2 border-r-2 w-16">
                          削除
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {techFolio?.tech_experiences?.map(
                        (techExperience, index) => (
                          <tr
                            key={techExperience.id || `new-${index}`}
                            onDragOver={handleDragOver}
                            onDrop={(e) =>
                              handleDrop(e, techExperience.sequence_num)
                            }
                          >
                            <td
                              draggable
                              onDragStart={(e) =>
                                handleDragStart(e, techExperience.sequence_num)
                              }
                              className={`${
                                index % 2 === 0 ? "bg-slate-100" : "bg-eaf0f6"
                              } px-4 py-2 text-center border-r-2 cursor-grab active:cursor-grabbing`}
                            >
                              <div className="flex flex-col items-center justify-center h-full">
                                <FontAwesomeIcon
                                  icon={faBars}
                                  className="text-slate-300 mb-1"
                                />
                                <span className="mt-auto">
                                  {techExperience.sequence_num}
                                </span>
                              </div>
                            </td>
                            <td
                              className={`${
                                index % 2 === 0 ? "bg-white" : "bg-slate-50"
                              } px-4 py-2 border-r-2 align-top`}
                            >
                              <div>
                                <strong>業界</strong>
                                <input
                                  type="text"
                                  value={techExperience.industry}
                                  onChange={(e) =>
                                    handleChangeTechExperience(
                                      techExperience.sequence_num,
                                      "industry",
                                      e.target.value,
                                    )
                                  }
                                  className="w-full border-2 border-slate-300 px-2 py-1 rounded focus:outline-none focus:ring focus:border-blue-300"
                                  maxLength={MAX_LENGTH.input.default}
                                />
                              </div>
                              <div>
                                <strong>プロジェクト名</strong>
                                <input
                                  type="text"
                                  value={techExperience.system_name}
                                  onChange={(e) =>
                                    handleChangeTechExperience(
                                      techExperience.sequence_num,
                                      "system_name",
                                      e.target.value,
                                    )
                                  }
                                  className="w-full border-2 border-slate-300 px-2 py-1 rounded focus:outline-none focus:ring focus:border-blue-300"
                                  maxLength={MAX_LENGTH.input.default}
                                />
                              </div>
                              <div>
                                <strong>システム概要</strong>
                                <textarea
                                  ref={initTextareaAutoResize}
                                  value={techExperience.system_detail}
                                  onChange={(e) => {
                                    handleChangeTechExperience(
                                      techExperience.sequence_num,
                                      "system_detail",
                                      e.target.value,
                                    );
                                    handleTextareaAutoResize(e);
                                  }}
                                  onInput={(e) =>
                                    handleTextareaAutoResize(
                                      e as React.ChangeEvent<HTMLTextAreaElement>,
                                    )
                                  }
                                  className="w-full min-h-[5rem] border-2 border-slate-300 px-2 py-1 rounded focus:outline-none focus:ring focus:border-blue-300 resize-none overflow-hidden"
                                  maxLength={MAX_LENGTH.textarea.default}
                                />
                              </div>
                              <div>
                                <strong>担当業務</strong>
                                <input
                                  type="text"
                                  value={techExperience.assigned_task}
                                  onChange={(e) =>
                                    handleChangeTechExperience(
                                      techExperience.sequence_num,
                                      "assigned_task",
                                      e.target.value,
                                    )
                                  }
                                  className="w-full border-2 border-slate-300 px-2 py-1 rounded focus:outline-none focus:ring focus:border-blue-300"
                                  maxLength={MAX_LENGTH.input.default}
                                />
                              </div>
                            </td>
                            <td
                              className={`${
                                index % 2 === 0 ? "bg-white" : "bg-slate-50"
                              } px-4 py-2 border-r-2 align-top`}
                            >
                              {techExperience.work_contents?.map(
                                (content, idx) => {
                                  // 選択済みの ID を取得（現在の行を除外）
                                  const selectedIds =
                                    techExperience.work_contents
                                      ?.filter((_, i) => i !== idx)
                                      .map((wc) => wc.id) || [];

                                  // フィルタリングされた選択肢（現在の値は必ず含む）
                                  const filteredOptions =
                                    workContentOptions.filter(
                                      (option) =>
                                        !selectedIds.includes(option.id) ||
                                        option.id === content.id,
                                    );

                                  return (
                                    <div
                                      className="flex pb-2 w-full"
                                      key={content.rowKey}
                                    >
                                      <select
                                        value={content.id}
                                        onChange={(e) =>
                                          handleChangeWorkContent(
                                            techExperience.sequence_num,
                                            idx,
                                            parseInt(e.target.value, 10),
                                            e.target.options[
                                              e.target.selectedIndex
                                            ].text,
                                          )
                                        }
                                        className="w-full border-2 border-slate-300 px-2 py-1 rounded focus:outline-none focus:ring focus:border-blue-300"
                                      >
                                        <option value="0" disabled>
                                          作業内容を選択
                                        </option>
                                        {filteredOptions.map((option) => (
                                          <option
                                            key={option.id}
                                            value={option.id}
                                          >
                                            {option.name}
                                          </option>
                                        ))}
                                      </select>
                                      <div className="pl-2 py-1">
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleDeleteWorkContent(
                                              techExperience.sequence_num,
                                              content.rowKey!,
                                            )
                                          }
                                          className="flex items-center justify-center w-6 h-6 bg-slate-600 text-white rounded-full hover:bg-slate-300"
                                        >
                                          <FontAwesomeIcon icon={faTimes} />
                                        </button>
                                      </div>
                                    </div>
                                  );
                                },
                              )}
                              <button
                                onClick={() =>
                                  handleAddWorkContent(
                                    techExperience.sequence_num,
                                  )
                                }
                                className="bg-gray-700 text-white font-bold py-2 px-4 rounded-3xl hover:bg-opacity-50"
                              >
                                <FontAwesomeIcon
                                  icon={faCirclePlus}
                                  className="mr-2"
                                />
                                追加
                              </button>
                            </td>
                            <td
                              className={`${
                                index % 2 === 0 ? "bg-white" : "bg-slate-50"
                              } px-4 py-2 border-r-2 align-top`}
                            >
                              <div>
                                <strong>役割</strong>
                                <select
                                  defaultValue={techExperience.position_id}
                                  onChange={(e) => {
                                    handleChangeTechExperience(
                                      techExperience.sequence_num,
                                      "position_id",
                                      parseInt(e.target.value, 10),
                                    );

                                    handleChangePosition(
                                      techExperience.sequence_num,
                                      "position",
                                      parseInt(e.target.value, 10),
                                      e.target.options[e.target.selectedIndex]
                                        .text,
                                    );
                                  }}
                                  className="border-2 w-full border-slate-300 px-2 py-1 rounded focus:outline-none focus:ring focus:border-blue-300"
                                >
                                  {positionOptions.map((option) => (
                                    <option key={option.id} value={option.id}>
                                      {option.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              {/* 
                            <div>
                              <strong>チーム</strong>
                              <input
                                type="text"
                                maxLength={MAX_LENGTH.input.default}
                                value={techExperience.team}
                                onChange={(e) =>
                                  handleChangeTechExperience(
                                    techExperience.sequence_num,
                                    "team",
                                    e.target.value
                                  )
                                }
                                className="w-full border-2 border-slate-300 px-2 py-1 rounded focus:outline-none focus:ring focus:border-blue-300"
                              />
                            </div>
                            */}
                              <div>
                                <strong>PJTチーム</strong>
                                <input
                                  type="number"
                                  min="1"
                                  step="1"
                                  value={techExperience.member_count}
                                  onChange={(e) =>
                                    handleChangeTechExperience(
                                      techExperience.sequence_num,
                                      "member_count",
                                      e.target.value,
                                    )
                                  }
                                  className="w-full border-2 border-slate-300 px-2 py-1 rounded focus:outline-none focus:ring focus:border-blue-300"
                                />
                              </div>
                            </td>
                            <td
                              className={`${
                                index % 2 === 0 ? "bg-white" : "bg-slate-50"
                              } px-4 py-2 border-r-2 align-top`}
                            >
                              {getLabelsBySkillType(
                                techExperience.skill_type,
                              ).map((label, i) => {
                                const envKey = `env${
                                  i + 1
                                }` as keyof typeof techExperience;
                                const value = techExperience[envKey];

                                return (
                                  <div key={i}>
                                    <strong className="inline-block">
                                      {label}
                                    </strong>
                                    <input
                                      type="text"
                                      value={
                                        typeof value === "string" ||
                                        typeof value === "number"
                                          ? value
                                          : ""
                                      }
                                      onChange={(e) =>
                                        handleChangeTechExperience(
                                          techExperience.sequence_num,
                                          envKey,
                                          e.target.value,
                                        )
                                      }
                                      className="w-full border-2 border-slate-300 px-2 py-1 rounded focus:outline-none focus:ring focus:border-blue-300"
                                      maxLength={MAX_LENGTH.input.default}
                                    />
                                    <br />
                                  </div>
                                );
                              })}
                            </td>
                            <td
                              className={`${
                                index % 2 === 0 ? "bg-white" : "bg-slate-50"
                              } px-4 py-2 border-r-2 align-top`}
                            >
                              <div className="my-1">
                                <strong>開始</strong>：
                              </div>
                              <div className="my-1">
                                <input
                                  type="month"
                                  value={techExperience.work_start_period}
                                  onChange={(e) =>
                                    handleChangeTechExperience(
                                      techExperience.sequence_num,
                                      "work_start_period",
                                      e.target.value,
                                    )
                                  }
                                  className="border border-gray-300 rounded p-2"
                                />
                              </div>
                              <div className="my-1">
                                <strong>
                                  終了
                                  {isPlaned(techExperience.sequence_num) &&
                                    "予定"}
                                </strong>
                                ：
                              </div>
                              <div className="my-1">
                                <input
                                  type="month"
                                  value={techExperience.work_end_period}
                                  onChange={(e) =>
                                    handleChangeTechExperience(
                                      techExperience.sequence_num,
                                      "work_end_period",
                                      e.target.value,
                                    )
                                  }
                                  className="border border-gray-300 rounded p-2"
                                />
                              </div>
                              <div className="my-1">
                                <strong>期間</strong>：
                              </div>
                              <div className="my-1">
                                {calculatePeriod(techExperience.sequence_num)}
                              </div>
                            </td>
                            <td
                              className={`${
                                index % 2 === 0 ? "bg-white" : "bg-slate-50"
                              } px-4 py-2 border-r-2 align-top max-w-32`}
                            >
                              <strong>取引先</strong>
                              <input
                                type="text"
                                value={techExperience.client}
                                onChange={(e) =>
                                  handleChangeTechExperience(
                                    techExperience.sequence_num,
                                    "client",
                                    e.target.value,
                                  )
                                }
                                className="w-full border-2 border-slate-300 px-2 py-1 rounded focus:outline-none focus:ring focus:border-blue-300"
                                maxLength={MAX_LENGTH.input.default}
                              />
                              <strong>エンドユーザー</strong>
                              <input
                                type="text"
                                value={techExperience.end_user}
                                onChange={(e) =>
                                  handleChangeTechExperience(
                                    techExperience.sequence_num,
                                    "end_user",
                                    e.target.value,
                                  )
                                }
                                className="w-full border-2 border-slate-300 px-2 py-1 rounded focus:outline-none focus:ring focus:border-blue-300"
                                maxLength={MAX_LENGTH.input.default}
                              />
                              {/* <strong>備考</strong>：
                            <textarea
                              value={techExperience.remarks}
                              onChange={(e) =>
                                handleChangeTechExperience(
                                  techExperience.sequence_num,
                                  "remarks",
                                  e.target.value
                                )
                              }
                              className="w-full min-h-24 border-2 border-slate-300 px-2 py-1 rounded focus:outline-none focus:ring focus:border-blue-300"
                            /> */}
                            </td>
                            <td
                              className={`${
                                index % 2 === 0 ? "bg-white" : "bg-slate-50"
                              } px-4 py-2 border-r-2`}
                            >
                              <div className="flex items-center justify-center h-full">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleDeleteTechExperience(
                                      techExperience.sequence_num,
                                    );
                                  }}
                                  className="flex items-center justify-center w-6 h-6 bg-slate-600 text-white rounded-full hover:bg-slate-300"
                                >
                                  <FontAwesomeIcon icon={faTimes} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="py-2 space-x-5">
                <button
                  onClick={() => handleAddTechExperience(1)}
                  className="bg-gray-700 text-white font-bold py-2 px-4 rounded-3xl hover:bg-opacity-50"
                >
                  <FontAwesomeIcon icon={faCirclePlus} className="mr-2" />
                  アプリ項目追加
                </button>
                <button
                  onClick={() => handleAddTechExperience(2)}
                  className="bg-gray-700 text-white font-bold py-2 px-4 rounded-3xl hover:bg-opacity-50"
                >
                  <FontAwesomeIcon icon={faCirclePlus} className="mr-2" />
                  インフラ項目追加
                </button>
                <button
                  onClick={() => handleAddTechExperience(3)}
                  className="bg-gray-700 text-white font-bold py-2 px-4 rounded-3xl hover:bg-opacity-50"
                >
                  <FontAwesomeIcon icon={faCirclePlus} className="mr-2" />
                  セキュリティ項目追加
                </button>
                <button
                  onClick={() => handleAddTechExperience(4)}
                  className="bg-gray-700 text-white font-bold py-2 px-4 rounded-3xl hover:bg-opacity-50"
                >
                  <FontAwesomeIcon icon={faCirclePlus} className="mr-2" />
                  デザイナー項目追加
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="fixed bottom-0 right-0 bg-gray-700 bg-opacity-30 text-white z-50 w-full h-16 flex items-center justify-end pr-10 space-x-5">
        <button
          onClick={showExample}
          className="ignore-dirty bg-gray-700 text-white font-bold py-2 px-4 rounded-3xl hover:bg-opacity-50"
        >
          <FontAwesomeIcon icon={faFileLines} className="mr-2" />
          記入例を確認
        </button>
        {/* <button
          onClick={showHistory}
          className="bg-gray-700 text-white font-bold py-2 px-4 rounded-3xl hover:bg-opacity-50"
        >
          <FontAwesomeIcon icon={faFileLines} className="mr-2" />
          前回登録のスキルシートを確認
        </button> */}
        <button
          onClick={openPrevModal}
          className="ignore-dirty bg-gray-700 text-white font-bold py-2 px-4 rounded-3xl hover:bg-opacity-50"
        >
          <FontAwesomeIcon icon={faPrint} className="mr-2" />
          印刷プレビュー
        </button>
        <button
          onClick={() => handleSave()}
          className="ignore-dirty bg-iso text-white font-bold py-2 px-4 rounded-3xl hover:bg-opacity-50"
        >
          <FontAwesomeIcon icon={faCircleCheck} className="mr-2" />
          保存
        </button>
      </div>

      {/* 印刷プレビューオプション選択モーダル */}
      <PrintPreviewModal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        title="印刷プレビューオプション"
        user={user}
        qualifications={qualifications}
        techFolio={techFolio}
      ></PrintPreviewModal>

      <ConfirmModal
        isOpen={isConfirmModalOpen}
        title="確認"
        text={modalText}
        onConfirm={() => {
          setConfirmModalOpen(false);
          confirmAction();
        }}
        onCancel={() => setConfirmModalOpen(false)}
      />

      <SuccessModal
        isOpen={isSuccessModalOpen}
        title="完了"
        text="保存が正常に完了しました。"
        onClose={() => setSuccessModalOpen(false)}
      />

      <ErrorModal
        isOpen={isErrorModalOpen}
        title="エラー"
        text={errorMessage}
        onClose={() => setErrorModalOpen(false)}
      />
    </Layout>
  );
};

export default MySheet;
